import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Sparkles, MapPin, Phone, Instagram, MessageCircle, CheckCircle2, ArrowRight, Dumbbell, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API });

const money = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;

export default function Landing() {
  const [data, setData] = useState(null);
  const [heroIdx, setHeroIdx] = useState(0);

  useEffect(() => {
    (async () => {
      try { const { data } = await http.get("/landing/public"); setData(data); } catch {}
    })();
  }, []);

  useEffect(() => {
    if (!data?.config?.hero_images?.length) return;
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % data.config.hero_images.length), 5000);
    return () => clearInterval(t);
  }, [data]);

  if (!data) return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-white"><Loader2 className="w-8 h-8 animate-spin"/></div>;
  const cfg = data.config;
  const primary = cfg.primary_color || "#E11D48";
  const secondary = cfg.secondary_color || "#F59E0B";
  const heroImg = cfg.hero_images?.[heroIdx];

  const openSignup = () => { window.location.href = "/autocadastro"; };
  const openWhatsApp = () => {
    if (!cfg.contact_whatsapp) return;
    const clean = String(cfg.contact_whatsapp).replace(/\D/g, "");
    const phone = clean.startsWith("55") ? clean : `55${clean}`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent("Olá! Quero saber mais sobre a academia.")}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        {heroImg ? (
          <>
            <img src={heroImg} alt="Academia" className="absolute inset-0 w-full h-full object-cover"/>
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/40"/>
          </>
        ) : (
          <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${primary} 0%, #0f172a 60%)` }}/>
        )}
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-32 w-full">
          <div className="flex items-center gap-3 mb-6">
            <img src="https://customer-assets-4nw71qhi.emergentagent.net/job_academia-control-3/artifacts/hiofilsr_LOGO%20AMERICANFIT.png" alt="American Fit" className="h-14"/>
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20 mb-5">
            <Sparkles className="w-3 h-3" style={{ color: primary }}/>
            <span className="text-[10px] uppercase tracking-[0.3em] font-bold">Matrículas abertas</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black uppercase leading-[0.95] max-w-3xl text-rose-500">{cfg.title}</h1>
          <p className="mt-4 text-xl md:text-2xl font-heading text-white/80 max-w-2xl">{cfg.subtitle}</p>
          <p className="mt-3 text-white/60 max-w-2xl text-base md:text-lg leading-relaxed">{cfg.description}</p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button onClick={openSignup} className="h-14 px-8 text-base md:text-lg font-heading font-black uppercase rounded-2xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.5)] hover:scale-[1.02] transition text-white" style={{ background: secondary }} data-testid="landing-cta-signup">
              Matricule-se agora <ArrowRight className="w-5 h-5 ml-2"/>
            </Button>
            {cfg.contact_whatsapp && (
              <Button onClick={openWhatsApp} variant="outline" className="h-14 px-6 text-base font-bold uppercase rounded-2xl bg-emerald-600/10 border-emerald-500/40 text-emerald-300 hover:bg-emerald-600/20" data-testid="landing-cta-whatsapp">
                <MessageCircle className="w-5 h-5 mr-2"/>Falar no WhatsApp
              </Button>
            )}
          </div>
          {cfg.hero_images?.length > 1 && (
            <div className="mt-8 flex items-center gap-1">
              {cfg.hero_images.map((_, i) => (
                <button key={i} onClick={() => setHeroIdx(i)} className={`h-1 rounded-full transition-all ${i === heroIdx ? "w-10" : "w-4 opacity-40"}`} style={{ background: secondary }}/>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Features */}
      {cfg.features?.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="text-center mb-10">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold mb-2" style={{ color: primary }}>Por que American Fit</div>
            <h2 className="font-heading text-3xl md:text-5xl font-black uppercase text-rose-500">Tudo o que sua rotina precisa</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="landing-features">
            {cfg.features.map((f, i) => (
              <Card key={i} className="p-6 bg-white/5 border-white/10 backdrop-blur">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${primary}22` }}>
                  <CheckCircle2 className="w-5 h-5" style={{ color: primary }}/>
                </div>
                <div className="font-bold text-white/90 leading-snug">{f}</div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Plans */}
      {data.plans?.length > 0 && (
        <section className="bg-slate-900/50 border-y border-white/5 py-16 md:py-24">
          <div className="max-w-6xl mx-auto px-6 md:px-10">
            <div className="text-center mb-10">
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold mb-2" style={{ color: primary }}>Planos</div>
              <h2 className="font-heading text-3xl md:text-5xl font-black uppercase text-rose-500">Escolha o seu</h2>
              <p className="text-white/60 mt-2 text-base md:text-lg">Pague no ato via Pix ou cartão · Comece a treinar hoje</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="landing-plans">
              {data.plans.map((p, i) => {
                const highlight = i === 1 && data.plans.length >= 3;
                return (
                  <Card key={p.id} className={`p-6 md:p-8 backdrop-blur transition-all hover:scale-[1.02] cursor-pointer ${highlight ? "bg-rose-950/40 border-rose-500 shadow-[0_20px_60px_-15px_rgba(225,29,72,0.5)]" : "bg-white/5 border-white/10"}`} onClick={openSignup} data-testid={`landing-plan-${p.id}`}>
                    {highlight && <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full mb-3 text-[10px] uppercase tracking-widest font-bold text-white" style={{ background: secondary }}>Mais popular</div>}
                    <div className="font-heading text-2xl md:text-3xl font-black uppercase mb-1 text-rose-500">{p.name}</div>
                    <div className="text-white/60 text-sm mb-5 min-h-[40px]">{p.description || `${p.duration_days ? `${p.duration_days} dias de acesso` : `${p.duration_months} mês(es) de acesso`}`}</div>
                    <div className="font-heading text-5xl font-black text-rose-500">{money(p.price)}</div>
                    <div className="text-xs text-white/50 mt-1 mb-6">{p.duration_days ? `${p.duration_days} dia(s)` : `${p.duration_months} mês(es)`}</div>
                    <Button className={`w-full h-12 rounded-2xl font-heading font-black uppercase text-white ${highlight ? "" : "bg-white/10 hover:bg-white/20"}`} style={highlight ? { background: secondary } : {}} data-testid={`landing-plan-cta-${p.id}`}>
                      Quero este plano <ArrowRight className="w-4 h-4 ml-2"/>
                    </Button>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Units */}
      {data.units?.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="text-center mb-10">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold mb-2" style={{ color: primary }}>Unidades</div>
            <h2 className="font-heading text-3xl md:text-5xl font-black uppercase text-rose-500">Onde treinar</h2>
            <p className="text-white/60 mt-2">Seu plano libera acesso a qualquer uma</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="landing-units">
            {data.units.map((u) => {
              const mapsQuery = encodeURIComponent(u.address ? `${u.name} — ${u.address}` : u.name);
              const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${mapsQuery}`;
              return (
                <Card key={u.id} className="p-5 bg-white/5 border-white/10 backdrop-blur hover:border-rose-500/40 transition">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${primary}22` }}>
                      <Dumbbell className="w-4 h-4" style={{ color: primary }}/>
                    </div>
                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`landing-unit-maps-${u.id}`}
                      title="Abrir no Google Maps"
                      className="font-heading text-lg font-black uppercase leading-tight text-rose-500 hover:text-rose-400 hover:underline underline-offset-4 decoration-rose-500/60 cursor-pointer inline-flex items-center gap-1.5"
                    >
                      {u.name}
                      <MapPin className="w-3.5 h-3.5 opacity-70"/>
                    </a>
                  </div>
                  {u.address && (
                    <a href={mapsUrl} target="_blank" rel="noreferrer" className="flex items-start gap-2 text-sm text-white/70 hover:text-white transition" data-testid={`landing-unit-address-${u.id}`}>
                      <MapPin className="w-4 h-4 mt-0.5 shrink-0"/><span>{u.address}</span>
                    </a>
                  )}
                  {u.phone && <div className="flex items-center gap-2 text-sm text-white/70 mt-1"><Phone className="w-4 h-4"/><span>{u.phone}</span></div>}
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <Card className="p-8 md:p-14 bg-gradient-to-br from-rose-950/60 via-slate-900 to-slate-950 border border-rose-500/30 text-center">
          <h2 className="font-heading text-3xl md:text-5xl font-black uppercase mb-3 text-rose-500">Pronto para começar?</h2>
          <p className="text-white/70 mb-6 text-base md:text-lg">Cadastro em 3 minutos · Pagamento por Pix na hora · Acesso liberado imediatamente</p>
          <Button onClick={openSignup} className="h-14 px-10 text-base md:text-lg font-heading font-black uppercase rounded-2xl text-white" style={{ background: secondary }} data-testid="landing-cta-bottom">
            Fazer minha matrícula agora <ArrowRight className="w-5 h-5 ml-2"/>
          </Button>
          <div className="mt-6 flex items-center justify-center flex-wrap gap-4 text-xs text-white/50">
            {cfg.contact_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3"/>{cfg.contact_phone}</span>}
            {cfg.contact_email && <span>✉️ {cfg.contact_email}</span>}
            {cfg.instagram && <a href={`https://instagram.com/${cfg.instagram.replace("@","")}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-white"><Instagram className="w-3 h-3"/>{cfg.instagram}</a>}
          </div>
        </Card>
      </section>

      <footer className="text-center py-8 text-xs text-white/30 uppercase tracking-widest border-t border-white/5">
        American Fit © {new Date().getFullYear()} · Site atualizado pelo painel administrativo
      </footer>
    </div>
  );
}
